import {useAtomValue} from 'jotai';
import {memo, MouseEvent, useCallback} from 'react';
import {getCategoryIcon} from '@/components/CategoryIcon';
import {KodataNearbyStoreItem} from '@/map/@types';
import {selectedNearbyStoreItemCardAtom} from '@/map/atom';
import {MAX_VISIBLE_ZOOM_METER} from '@/map/constants';
import {useVisibleZoomLevel} from '@/map/hooks';
import {convertToMyfcCategoryFromKodata} from '@/map/utils';
import {getCategoryId} from '@/utils';
import {SpotMarker} from '../Marker/SpotMarker';

type ItemWithPosition = KodataNearbyStoreItem & {position: naver.maps.Coord};
type Item = ItemWithPosition & {overlapped: ItemWithPosition[]};

interface IProps {
  map?: naver.maps.Map;
  items?: Item[];
  selectedItemsIndex?: number;
  onClick?(index: number): void;
}

interface MarkerProps {
  map: naver.maps.Map;
  id: string;
  item: Item;
  active?: boolean;
  selectedItemsIndex?: number;
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export const NearbyStoreMarkers = ({
  map,
  items,
  selectedItemsIndex,
  onClick,
}: IProps) => {
  const isVisibleZoom = useVisibleZoomLevel(
    MAX_VISIBLE_ZOOM_METER.mobile_markers
  );
  const selectedItem = useAtomValue(selectedNearbyStoreItemCardAtom);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const id = e.currentTarget.dataset.id;

      if (id) {
        if (id) {
          const idx = Number(id);
          onClick?.(idx);
        }
      }
    },
    [onClick]
  );

  if (!map || !items || !isVisibleZoom) {
    return null;
  }

  return (
    <>
      {items.map((i, idx) => (
        <Marker
          key={idx}
          map={map}
          id={String(idx)}
          active={selectedItemsIndex === idx}
          selectedItemsIndex={
            selectedItemsIndex === idx ? selectedItem?.index : -1
          }
          item={i}
          onClick={handleClick}
        />
      ))}
    </>
  );
};

const Marker = memo(({item, selectedItemsIndex, ...props}: MarkerProps) => {
  if (!item || !item.position) {
    return null;
  }

  const count = item.overlapped?.length;
  const mainItem =
    (selectedItemsIndex && selectedItemsIndex > 0
      ? item.overlapped[selectedItemsIndex - 1]
      : item) || item;
  const category = convertToMyfcCategoryFromKodata(item.category);
  const categoryId = getCategoryId(category);
  const name = mainItem.branchName;
  const icon = getCategoryIcon(categoryId, 22);

  return (
    <SpotMarker
      {...props}
      position={item.position}
      name={name}
      icon={icon}
      count={count}
      alwaysDisplayName={!count}
    />
  );
});
